import { CycleCountSessionItem } from '../models/cycle-count-session.model';

/**
 * A cycle-count item is "counted" (actioned) once it has an actual quantity, has
 * been marked Not Found, OR has a Broken/Damaged quantity. This is the single
 * definition the Hide-Completed filter and the uncounted-first sort (#31280) and
 * the admin delete gate (#31334) all key on, matching the server's
 * ValidateItemDeletion rule exactly. Note actualQty === 0 counts as counted (0 is
 * a real count), so the check is an explicit `!= null`, never a falsy test. In
 * practice a recorded count always sets actualQty (a Broken quantity is entered
 * alongside a count), so the damagedQty term never changes the filter/sort
 * outcome; it is here so the one predicate is also correct for the delete gate.
 */
export function isCycleCountItemCounted(item: CycleCountSessionItem): boolean {
  return (
    item.actualQty != null ||
    item.isNotFound === true ||
    (item.damagedQty != null && item.damagedQty > 0)
  );
}

/**
 * Orders uncounted items before counted ones (#31280) so a counter sees the
 * remaining work first. STABLE: within each partition the input order is
 * preserved, so the sub-location / material ordering the list was loaded in is
 * kept intact. Returns a new array — does not mutate the input.
 */
export function sortUncountedFirst(
  items: CycleCountSessionItem[]
): CycleCountSessionItem[] {
  return [
    ...items.filter((i) => !isCycleCountItemCounted(i)),
    ...items.filter((i) => isCycleCountItemCounted(i)),
  ];
}

/**
 * Drops completed items, keeping only uncounted ones (#31280 Hide Completed).
 * Fail-closed: an all-counted input yields an empty array, never the unfiltered
 * superset.
 */
export function filterUncountedItems(
  items: CycleCountSessionItem[]
): CycleCountSessionItem[] {
  return items.filter((i) => !isCycleCountItemCounted(i));
}

/** Minimal progress shape shared by the mobile sub-location group rows. */
export interface CountProgressGroup {
  counted: number;
  total: number;
}

/**
 * Orders incomplete sub-location groups (counted < total) before fully-counted
 * ones (#31280 mobile groups list). STABLE within each partition. A group with
 * total 0 is treated as complete (0 < 0 is false) so an empty bin is never
 * surfaced as remaining work. Returns a new array.
 */
export function sortIncompleteGroupsFirst<T extends CountProgressGroup>(
  groups: T[]
): T[] {
  return [
    ...groups.filter((g) => g.counted < g.total),
    ...groups.filter((g) => g.counted >= g.total),
  ];
}

/** Drops fully-counted groups, keeping only those with work remaining (#31280). */
export function filterIncompleteGroups<T extends CountProgressGroup>(
  groups: T[]
): T[] {
  return groups.filter((g) => g.counted < g.total);
}

/**
 * A sub-location group on the mobile cycle-count session: its code/name, the
 * items in it, and how many are counted. Extends CountProgressGroup so the
 * incomplete-first sort/filter above apply directly.
 */
export interface SubLocationGroup extends CountProgressGroup {
  code: string;
  name: string;
  items: CycleCountSessionItem[];
}

/**
 * Aggregates session items into sub-location groups (keyed by subLocationCode),
 * tallying total and counted per group, ordered by code. Extracted from the
 * mobile session component (#31280) so the aggregation is unit-tested and the
 * component keeps only its view wiring. `counted` uses the shared
 * isCycleCountItemCounted definition (actualQty set OR Not Found).
 */
export function buildSubLocationGroups(
  items: CycleCountSessionItem[]
): SubLocationGroup[] {
  const map = new Map<string, SubLocationGroup>();
  for (const item of items) {
    const code = item.subLocationCode ?? '';
    let group = map.get(code);
    if (!group) {
      group = {
        code,
        name: item.subLocationName || code || '—',
        total: 0,
        counted: 0,
        items: [],
      };
      map.set(code, group);
    }
    group.items.push(item);
    group.total++;
    if (isCycleCountItemCounted(item)) group.counted++;
  }
  return Array.from(map.values()).sort((a, b) => a.code.localeCompare(b.code));
}
